
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";

export interface BlockedCompany {
  id: string;
  company_name: string;
  domain: string;
  created_at: string;
  notes?: string;
  organization_id?: string | null;
}

export function useBlockedCompanies(organizationId: string | undefined) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const {
    data: blockedCompanies,
    isLoading,
    error
  } = useQuery({
    queryKey: ['blocked-companies', organizationId, user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      console.log('Fetching blocked companies for:', 
        organizationId ? `organization: ${organizationId}` : `user: ${user.id}`);
      
      let query = supabase
        .from('blocked_companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      } else {
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching blocked companies:', error);
        throw error;
      }
      
      console.log(`Found ${data?.length || 0} blocked companies`);
      return data as BlockedCompany[];
    },
    enabled: !!user?.id
  });

  const blockCompany = useMutation({
    mutationFn: async ({ 
      companyName, 
      domain, 
      notes,
      organizationId: overrideOrgId
    }: { 
      companyName: string; 
      domain: string; 
      notes?: string;
      organizationId?: string;
    }) => {
      const effectiveOrgId = overrideOrgId || organizationId;
      
      if (!companyName || !domain) {
        throw new Error("Missing required data");
      }
      
      if (!user?.id) {
        console.error("No user ID available");
        throw new Error("User not authenticated");
      }
      
      const blockData = {
        organization_id: effectiveOrgId || null,
        user_id: user.id,
        company_name: companyName,
        domain,
        notes
      };

      console.log("Blocking with data:", blockData);

      const { error, data } = await supabase
        .from('blocked_companies')
        .insert(blockData);

      if (error) {
        console.error("Error inserting blocked company:", error);
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-companies'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-enriched-visitors'] });
      toast.success("Company blocked successfully");
    },
    onError: (error) => {
      console.error('Error blocking company:', error);
      toast.error("Failed to block company");
    }
  });

  const unblockCompany = useMutation({
    mutationFn: async (companyId: string) => {
      const { error } = await supabase
        .from('blocked_companies')
        .delete()
        .eq('id', companyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-companies'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-enriched-visitors'] });
      toast.success("Company unblocked successfully");
    },
    onError: (error) => {
      console.error('Error unblocking company:', error);
      toast.error("Failed to unblock company");
    }
  });

  return {
    blockedCompanies,
    isLoading,
    error,
    blockCompany,
    unblockCompany
  };
}
